import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }
  .stat-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .stat-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 10px;
  }
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2C2C2C;
    letter-spacing: -0.03em;
    line-height: 1;
  }
  .stat-sub {
    font-size: 0.8rem;
    color: #888;
    margin-top: 6px;
  }
  .section-title {
    font-size: 1rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 16px;
    letter-spacing: -0.01em;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8;
  }
  .data-table td {
    padding: 13px 16px;
    font-size: 0.9rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr:hover td { background: #FDFCFB; }
  .badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .badge-sensitive {
    background: #FFF0EE;
    color: #C0392B;
  }
  .badge-ok {
    background: #F0FFF4;
    color: #27AE60;
  }
  .loading-state {
    color: #888;
    font-size: 0.9rem;
    padding: 20px 0;
  }
  .error-state {
    color: #C0392B;
    font-size: 0.9rem;
    padding: 20px 0;
  }
`;

function fmt(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [convs, setConvs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminApi.stats(), adminApi.listConversations()])
      .then(([s, c]) => {
        setStats(s);
        setConvs((c || []).slice(0, 10));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout title="Dashboard">
      <style>{styles}</style>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {stats && (
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{fmt(stats.total_users)}</div>
            <div className="stat-sub">Family accounts</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active This Week</div>
            <div className="stat-value">{fmt(stats.active_this_week)}</div>
            <div className="stat-sub">≥1 conversation</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Conversations</div>
            <div className="stat-value">{fmt(stats.conversations_this_month)}</div>
            <div className="stat-sub">This month</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Voice Tokens</div>
            <div className="stat-value">{fmt(stats.tokens_this_month)}</div>
            <div className="stat-sub">
              ~${(stats.estimated_cost_usd || 0).toFixed(2)} estimated
            </div>
          </div>
          {stats.intake_mia && (
            <div className="stat-card">
              <div className="stat-label">Mia Intake Tokens</div>
              <div className="stat-value">{fmt(stats.intake_mia.total_tokens)}</div>
              <div className="stat-sub">
                ~${(stats.intake_mia.estimated_cost_usd || 0).toFixed(4)} estimated
              </div>
            </div>
          )}
        </div>
      )}

      <div className="section-title">Recent Activity</div>
      {convs.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Date</th>
              <th>Duration</th>
              <th>Turns</th>
              <th>Sensitive</th>
            </tr>
          </thead>
          <tbody>
            {convs.map(c => (
              <tr key={c.id}>
                <td>{c.user_name}</td>
                <td>{fmtDate(c.started_at)}</td>
                <td>{c.duration_mins > 0 ? `${c.duration_mins}m` : '—'}</td>
                <td>{c.turn_count}</td>
                <td>
                  {c.is_sensitive
                    ? <span className="badge badge-sensitive">Yes</span>
                    : <span className="badge badge-ok">No</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (!loading && <p className="loading-state">No recent conversations.</p>)}
    </AdminLayout>
  );
}
